import React from 'react';
import {useTranslation} from 'react-i18next';
import {authService} from '@/features/auth/services/authService';
import {API_BASE_URL} from '@/shared/apiClient';
import {webSocketSingleton} from '@/shared/api/WebSocketSingleton';
import {createEmbedAccessController,embedHeaders,embedRequested,setEmbedLease,SAI_ORIGIN} from './embedAccess';

export function SaiEmbedGate({children}:{children:React.ReactNode}){
 const {t}=useTranslation();
 const required=embedRequested();
 const [allowed,setAllowed]=React.useState(!required);
 React.useEffect(()=>{
  if(!required)return;
  const workspace=new URLSearchParams(window.location.search).get('workspace')||'';
  const originalFetch=window.fetch.bind(window);
  let independentToken:string|null=null;
  const controller=createEmbedAccessController({workspace,parent:window.parent,now:()=>Date.now(),nonce:()=>crypto.randomUUID(),
   post:message=>window.parent.postMessage(message,SAI_ORIGIN),
   retire:()=>{setEmbedLease(null);webSocketSingleton.clearSession();setAllowed(false);},
   exchange:async grant=>{
    let standalone=independentToken||authService.getToken()||await authService.refreshAccessToken();
    if(!standalone)throw Error('Sign in to Project Manager first');
    const exchange=()=>originalFetch(`${API_BASE_URL}/api/v1/sai-embed/session`,{method:'POST',credentials:'omit',
      headers:{Authorization:`Bearer ${standalone}`,'X-SAI-Projects-Grant':grant,'Content-Type':'application/json'}});
    let response=await exchange();
    if(response.status===401){
      standalone=await authService.refreshAccessToken();
      if(!standalone)throw Error('Sign in to Project Manager first');
      response=await exchange();
    }
    independentToken=standalone;
    if(!response.ok)throw Error('Embedded access denied');return response.json();
   },
  });
  const unsubscribe=controller.subscribe(()=>{const lease=controller.current();setEmbedLease(lease);setAllowed(Boolean(lease));});
  const fetchWithEmbed:typeof fetch=async(input,init)=>{
   const url=new URL(input instanceof Request?input.url:String(input),window.location.href);
   if(url.origin!==window.location.origin||!url.pathname.startsWith('/api/'))return originalFetch(input,init);
   // The grant exchange may renew the independent PM cookie. No application
   // request may silently fall back to that unconstrained access token.
   if(url.pathname==='/api/v1/auth/refresh')return originalFetch(input,init);
   const headers=new Headers(input instanceof Request?input.headers:undefined);
   new Headers(init?.headers).forEach((value,key)=>headers.set(key,value));
   Object.entries(embedHeaders()).forEach(([key,value])=>headers.set(key,value));
   return originalFetch(input,{...init,headers});
  };
  window.fetch=fetchWithEmbed;
  const leaseBridge=()=>controller.current();
  Object.defineProperty(window,'__pmSaiEmbedLease',{value:leaseBridge,configurable:true});
  const receive=(event:MessageEvent)=>{void controller.receive(event);};
  const retire=()=>{
    setAllowed(false);webSocketSingleton.clearSession();
    // Replacing this embedded document cancels pending frame/network work and
    // removes nested Office frames without touching the standalone PM session.
    window.location.replace('about:blank');
  };
  const monitor=controller.subscribe(()=>{
    if(controller.current())return;
    if(document.documentElement.dataset.saiProjectsAdmitted==='true')retire();
  });
  const mark=controller.subscribe(()=>{if(controller.current())document.documentElement.dataset.saiProjectsAdmitted='true';});
  window.addEventListener('message',receive);controller.start();
  return()=>{window.removeEventListener('message',receive);unsubscribe();monitor();mark();controller.stop();Reflect.deleteProperty(window,'__pmSaiEmbedLease');if(window.fetch===fetchWithEmbed)window.fetch=originalFetch;};
 },[required]);
 if(!allowed)return <div role="status" style={{padding:32,fontFamily:'system-ui',background:'#171717',color:'#f5f5f5',minHeight:'100vh'}}>
  <h1>{t('Projects access')}</h1><p>{t('Waiting for current SAI workspace access. Use the same verified email in SAI and Project Manager.')}</p>
  <a href="https://project-manager.dope-ai.kr/" target="_blank" rel="noopener noreferrer">{t('Sign in to Project Manager')}</a>
 </div>;
 return <>{children}</>;
}
